import * as React from 'react'

import { useUniqueId, useTimer } from '../../Utility'
import { moveFocusToContainer, focusInitial, Fence, isFence } from './utils'

/*

    NavigationTrap

    This component traps tabbing inside of a container.

    The normal usage is to wait until the "end" of initial render (this may be at end of animation
    and/or end of initial popover display) and then re-render with enabled={true}

    A simple example:

        import { NavigationTrap } from '@ds/react-utils'

        export const MyDialog = () => {

          const [trappingEnabled, setTrappingEnabled] = useState<boolean>(false)

          React.useEffect(() => {
            setTrappingEnabled(true)
          },[setTrappingEnabled])

          return (
              <NavigationTrap enabled={trappingEnabled} >
                <h2>My Cool Dialog</h2>
                Enter some stuff:
                <input type="text">
                <button>Close</button>
            </NavigationTrap>
          )
        }

    NavigationTrap ensures that the trap is eliminated when the component unmounts. The component that
    had focus when the trap was initially rendered is re-focused.

    By default initial focus is placed on the first navigable item. However, the consumer can use the
    initialFocus prop to determine which element gets focus.

    Nesting is supported.

    If the NavigationTrap children does not have any navigable content the component will still
    trap the tabbing inside of the trap (effectively disabling tabbing). However this is not a
    recommended choice.  This "no navigables" handling is not supported in InternetExplorer (the
    user will be able to navigate out of the container).

*/

export interface NavigationTrapProps {
  /**
   * The content to display in the NavigationTrap
   */
  children: React.ReactNode
  /**
   * A custom string to override the default unique ID on the data-qa and the data-focus-fence attributes of the focus fence.
   */
  trapId?: string
  /**
   * By default NavigationTrap will return focus to the element that had focus prior to the trap's activation.  You can disable this behavior and manage focus yourself by setting this value to `true`.
   */
  disableFocusReturn?: boolean
  /**
   * If enabled tabbing will be trapped in the component
   */
  enabled?: boolean
  /**
   * Element (element or selector) which should override the default focus assignment.
   */
  initialElement?: HTMLElement | string
  /**
   * A boolean value indicating whether or not the browser should scroll the document to bring
   * the newly-focused element into view. A value of false for preventScroll (the default) means
   * that the browser will scroll the element into view after focusing it. If preventScroll is set
   * to true, no scrolling will occur when the trap is mounted or unmounted.
   */
  preventScroll?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-qa'?: string
}

/** Renders a wrapper which traps navigation */
export const NavigationTrap: React.FC<NavigationTrapProps> = ({
  children,
  trapId,
  disableFocusReturn = false,
  enabled = false,
  initialElement,
  preventScroll = false,
  'data-qa': qa = 'focus-trap-container',
}) => {
  const [focusContainer, setFocusContainer] = React.useState<HTMLDivElement>()
  const previousFocusRef = React.useRef<HTMLElement | null>()

  const uniqueId = useUniqueId('short')
  const navTrapId = trapId || uniqueId

  const { runLater: deferFocus } = useTimer()

  const focusUpdater = React.useCallback(
    (element: HTMLElement) =>
      deferFocus(() => {
        if (document.activeElement !== element) {
          element.focus({ preventScroll })
        }
      }),
    [deferFocus, preventScroll]
  )

  const previousFocusElement = previousFocusRef.current

  const trap = React.useCallback(() => {
    if (focusContainer) {
      focusInitial(navTrapId, focusContainer, focusUpdater, initialElement)
    }
  }, [navTrapId, focusContainer, initialElement, focusUpdater])

  const unTrap = React.useCallback(() => {
    // only attempt to restore the previous focus if the element is still in the dom
    if (
      !disableFocusReturn &&
      previousFocusElement &&
      document.body.contains(previousFocusElement)
    ) {
      previousFocusElement.focus({ preventScroll })
    }
  }, [disableFocusReturn, preventScroll, previousFocusElement])

  const handleFenceFocus = React.useCallback(
    (event: React.FocusEvent) => {
      const fence = event.target as HTMLElement
      if (focusContainer && isFence(fence, navTrapId)) {
        event.preventDefault()
        event.stopPropagation()
        moveFocusToContainer(navTrapId, focusContainer, focusUpdater, fence)
      }
    },
    [focusContainer, navTrapId, focusUpdater]
  )

  const handleFenceBlur = React.useCallback(
    (event: React.FocusEvent) => {
      /**
       * If a fence is losing focus and the new target is
       * NOT in the focus container then there are zero navigables
       * in the focus container => "revert" the new focus.
       */
      const fence = event.target as HTMLElement
      const focusTarget = event.relatedTarget as HTMLElement
      if (
        focusContainer &&
        isFence(fence, navTrapId) &&
        !focusContainer.contains(focusTarget as Node)
      ) {
        event.preventDefault()
        event.stopPropagation()
        fence.focus({ preventScroll })
      }
    },
    [focusContainer, navTrapId, preventScroll]
  )

  React.useEffect(() => {
    // only save previous once (if the trap is reset the previous focus will not be lost)
    if (previousFocusRef.current === undefined) {
      previousFocusRef.current = (document.activeElement as HTMLElement) ?? null
    }
  }, [])

  React.useEffect(() => {
    if (focusContainer && enabled) {
      trap()
    }
    return () => unTrap()
  }, [enabled, focusContainer, trap, unTrap])

  return (
    <>
      {enabled && (
        <Fence
          trapId={navTrapId}
          onFocus={handleFenceFocus}
          onBlur={handleFenceBlur}
          qa={`${navTrapId}-focus-fence-before`}
        />
      )}
      <div ref={(el) => setFocusContainer(el ?? undefined)} data-qa={qa}>
        {children}
      </div>
      {enabled && (
        <Fence
          trapId={navTrapId}
          onFocus={handleFenceFocus}
          onBlur={handleFenceBlur}
          qa={`${navTrapId}-focus-fence-after`}
        />
      )}
    </>
  )
}
